// decoder.js — 全 WGSL 解碼：hashgrid → fused MLP → 4 decode MLP → 組裝，單一 WebGPU device。
// g 留在 GPU 不回 CPU；只讀回壓實後的可見高斯（ply-ready 17 floats/高斯）。
// 取代 onnxruntime（wasm 30s / webgpu 315s）→ 目標毫秒級。
// 來源：4dgs-clipgstream_viewer/decoder.js（React 整合改動：pipeline 快取綁定 device、
// createSharedDevice 增加 onGpuInfo callback 取代 #gpu DOM 依賴）。

// ====== 幀 buffer 格式開關 ======
// true  = f16（每幀 ~112MB，容量翻倍，畫質幾乎無損）
// false = f32（每幀 ~224MB，原樣）
export const FRAME_F16 = true;
// ================================

// 把 bOut 的 f32 高斯記錄（17/顆）打包成 f16（9 個 u32 = 18 個 f16 槽，最後 1 槽 padding）
const WGSL_PACK = `
@group(0) @binding(0) var<storage, read> src: array<f32>;
@group(0) @binding(1) var<storage, read_write> dst: array<u32>;
@group(0) @binding(2) var<uniform> cnt: vec4<u32>;
@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x; if (i >= cnt.x) { return; }
  let s = i*17u; let d = i*9u;
  for (var k=0u; k<8u; k=k+1u) { dst[d+k] = pack2x16float(vec2<f32>(src[s+k*2u], src[s+k*2u+1u])); }
  dst[d+8u] = pack2x16float(vec2<f32>(src[s+16u], 0.0));
}`;

// cache-busting：讀完 manifest 後把 manifest.version 塞進來，資產 URL 加 ?v= → 重 deploy 後強制抓新檔
let _assetV = '';
export function setAssetVersion(v) { _assetV = v == null ? '' : String(v); }
function bust(u) { return _assetV ? u + (u.includes('?') ? '&' : '?') + 'v=' + _assetV : u; }
async function fbuf(u) { const r = await fetch(bust(u)); if (!r.ok) throw new Error('fetch ' + u); return r.arrayBuffer(); }
function catF32(a, b) { const o = new Float32Array(a.length + b.length); o.set(a, 0); o.set(b, a.length); return o; }
function catU16(a, b) { const o = new Uint16Array(a.length + b.length); o.set(a, 0); o.set(b, a.length); return o; }

// 共用參考 base（anchor/feat/scaling）只載一次，跨所有 clip 共用（CPU 資料，跨 device 可重用）
const _baseCache = {};
// 編譯好的 WGSL pipeline 快取：所有 clip 用同一份 shader，編一次就好（省每 clip 重編的時間）。
// pipeline 綁定建立它的 device — component 卸載會 destroy device，所以快取必須連 device 一起驗證。
const _pipeCache = {};
async function loadBase(manifest, log) {
  const key = manifest._dataRoot;
  if (_baseCache[key]) return _baseCache[key];
  log('載入共用參考 base（只一次）…');
  const bdir = `${manifest._dataRoot}/${manifest.base.dir}`;
  const [pA, pF, pS] = await Promise.all([fbuf(`${bdir}/anchor.f32`), fbuf(`${bdir}/feat.f16`), fbuf(`${bdir}/scaling.f16`)]);
  return _baseCache[key] = { anchor: new Float32Array(pA), feat: new Uint16Array(pF), scaling: new Uint16Array(pS), N: manifest.base.n_anchors };
}

// pass1：hashgrid → outG[N*64]
const WGSL_HASH = `
struct Meta { N:u32, nLevels:u32, F:u32, hashmap:u32, bmin:vec4<f32>, bmax:vec4<f32>, time:vec4<f32> };
@group(0) @binding(0) var<uniform> uni: Meta;
@group(0) @binding(1) var<storage, read> params: array<u32>;   // f16 packed（2 個 f16/u32）
@group(0) @binding(2) var<storage, read> anchors: array<f32>;
@group(0) @binding(3) var<storage, read> res: array<u32>;
@group(0) @binding(4) var<storage, read> dense: array<u32>;
@group(0) @binding(5) var<storage, read> entryOff: array<u32>;
@group(0) @binding(6) var<storage, read> scales: array<f32>;
@group(0) @binding(7) var<storage, read_write> outG: array<f32>;
const P1:u32=2654435761u; const P2:u32=805459861u; const P3:u32=3674653429u;
@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x; if (i >= uni.N) { return; }
  let ax=anchors[i*3u]; let ay=anchors[i*3u+1u]; let az=anchors[i*3u+2u];
  var pos4 = vec4<f32>((ax-uni.bmin.x)/(uni.bmax.x-uni.bmin.x),(ay-uni.bmin.y)/(uni.bmax.y-uni.bmin.y),
                       (az-uni.bmin.z)/(uni.bmax.z-uni.bmin.z), uni.time.x);
  for (var l:u32=0u; l<uni.nLevels; l=l+1u) {
    let sc=scales[l]; let r=res[l]; let off=entryOff[l]; let isDense=dense[l]==1u;
    var base: vec4<f32>; var frac: vec4<f32>;
    for (var d:u32=0u; d<4u; d=d+1u) { let p=pos4[d]*sc+0.5; let b=floor(p); base[d]=b; frac[d]=p-b; }
    var acc=vec4<f32>(0.0);
    for (var c:u32=0u; c<16u; c=c+1u) {
      var w=1.0; var g: array<u32,4>;
      for (var d:u32=0u; d<4u; d=d+1u) { let bit=(c>>d)&1u; g[d]=u32(base[d])+bit; w=w*select(1.0-frac[d],frac[d],bit==1u); }
      var idx:u32;
      if (isDense) { idx=g[0]+g[1]*r+g[2]*r*r+g[3]*r*r*r; }
      else { idx=((g[0]*1u)^(g[1]*P1)^(g[2]*P2)^(g[3]*P3))%uni.hashmap; }
      let bi2=(off+idx)*(uni.F/2u);   // base_i/2（F=4 → 每 entry 2 個 u32）
      let pa=unpack2x16float(params[bi2]); let pb=unpack2x16float(params[bi2+1u]);
      acc=acc+w*vec4<f32>(pa.x,pa.y,pb.x,pb.y);
    }
    let o=i*(uni.nLevels*uni.F)+l*uni.F; outG[o]=acc.x; outG[o+1u]=acc.y; outG[o+2u]=acc.z; outG[o+3u]=acc.w;
  }
}`;

// pass2：fused + 4 decode MLP + 組裝 + 壓實。維度從 manifest.decode_dims 動態生成（不寫死 n_offsets）。
// 一個 workgroup(128 threads) 處理 DEC_B 個 anchor：權重從 dw/fw 讀「一次」給 B 個 anchor 共用（攤平權重頻寬）。
const DEC_B = 4;
function buildDecodeWGSL(manifest, Dg) {
  const B = DEC_B;
  const dims = manifest.decode_dims, order = ['opacity', 'color', 'cov', 'offset'];
  const IN = dims.opacity.in, FD = Dg, FS = IN - FD;
  const FH = (manifest.fused_dims && manifest.fused_dims.W0[1]) || 128;
  let off = 0; const O = {};
  for (const nm of order) { const d = dims[nm];
    O[nm] = { W0: off, b0: off + d.in*d.hidden, W1: off + d.in*d.hidden + d.hidden,
              b1: off + d.in*d.hidden + d.hidden + d.hidden*d.out, H: d.hidden, OUT: d.out, SIG: d.nseq >= 4 };
    off += d.in*d.hidden + d.hidden + d.hidden*d.out + d.out;
  }
  const maxH = Math.max(...order.map(n => dims[n].hidden));
  const mlp = (nm, S) => { const o = O[nm]; return `
  for (var h=tid; h<${o.H}u; h=h+128u){
    var acc: array<f32,${B}>;
    for (var a=0u;a<${B}u;a=a+1u){ acc[a]=dw[${o.b0}u+h]; }
    for (var k=0u;k<${IN}u;k=k+1u){ let w=dw[${o.W0}u+k*${o.H}u+h];
      for (var a=0u;a<${B}u;a=a+1u){ acc[a]=acc[a]+feat[a*${IN}u+k]*w; } }
    for (var a=0u;a<${B}u;a=a+1u){ hid[a*${maxH}u+h]=max(acc[a],0.0); }
  }
  workgroupBarrier();
  for (var oo=tid; oo<${o.OUT}u; oo=oo+128u){
    var acc: array<f32,${B}>;
    for (var a=0u;a<${B}u;a=a+1u){ acc[a]=dw[${o.b1}u+oo]; }
    for (var h=0u;h<${o.H}u;h=h+1u){ let w=dw[${o.W1}u+h*${o.OUT}u+oo];
      for (var a=0u;a<${B}u;a=a+1u){ acc[a]=acc[a]+hid[a*${maxH}u+h]*w; } }
    for (var a=0u;a<${B}u;a=a+1u){ ${S}[a*${o.OUT}u+oo]=${o.SIG ? 'sig(acc[a])' : 'acc[a]'}; }
  }
  workgroupBarrier();`; };
  return `
struct D { N:u32, K:u32, gridW:u32, keep:f32 };
@group(0) @binding(0) var<uniform> d: D;
@group(0) @binding(1) var<storage, read> g: array<f32>;
@group(0) @binding(2) var<storage, read> sfeat: array<u32>;
@group(0) @binding(3) var<storage, read> scaling: array<u32>;
@group(0) @binding(4) var<storage, read> anchor: array<f32>;
@group(0) @binding(5) var<storage, read> fw: array<f32>;
@group(0) @binding(6) var<storage, read> dw: array<f32>;
@group(0) @binding(7) var<storage, read_write> outv: array<f32>;
@group(0) @binding(8) var<storage, read_write> counter: atomic<u32>;
const C0:f32 = 0.28209479177387814;
fn sig(x:f32)->f32 { return 1.0/(1.0+exp(-x)); }
var<workgroup> feat: array<f32,${B*IN}>;
var<workgroup> h0:   array<f32,${B*FH}>;
var<workgroup> hid:  array<f32,${B*maxH}>;
var<workgroup> sop:  array<f32,${B*O.opacity.OUT}>;
var<workgroup> scol: array<f32,${B*O.color.OUT}>;
var<workgroup> scov: array<f32,${B*O.cov.OUT}>;
var<workgroup> sofs: array<f32,${B*O.offset.OUT}>;
@compute @workgroup_size(128)
fn main(@builtin(workgroup_id) wid: vec3<u32>, @builtin(local_invocation_id) lid: vec3<u32>) {
  let wg = wid.x + wid.y * d.gridW;
  let i0 = wg * ${B}u;
  let tid = lid.x;
  for (var idx=tid; idx<${B*FS}u; idx=idx+128u){ let a=idx/${FS}u; let k=idx%${FS}u; let i=min(i0+a, d.N-1u);
    let si=i*${FS}u+k; let su=sfeat[si>>1u]; feat[a*${IN}u+k]=select(unpack2x16float(su).x, unpack2x16float(su).y, (si&1u)==1u); }
  workgroupBarrier();
  for (var o=tid; o<${FH}u; o=o+128u){ var acc: array<f32,${B}>; for (var a=0u;a<${B}u;a=a+1u){ acc[a]=0.0; }
    for (var k=0u;k<${FD}u;k=k+1u){ let w=fw[k*${FH}u+o]; for (var a=0u;a<${B}u;a=a+1u){ let i=min(i0+a,d.N-1u); acc[a]=acc[a]+g[i*${FD}u+k]*w; } }
    for (var a=0u;a<${B}u;a=a+1u){ h0[a*${FH}u+o]=max(acc[a],0.0); } }
  workgroupBarrier();
  for (var o=tid; o<${FD}u; o=o+128u){ var acc: array<f32,${B}>; for (var a=0u;a<${B}u;a=a+1u){ acc[a]=0.0; }
    for (var h=0u;h<${FH}u;h=h+1u){ let w=fw[${FD*FH}u+h*${FD}u+o]; for (var a=0u;a<${B}u;a=a+1u){ acc[a]=acc[a]+h0[a*${FH}u+h]*w; } }
    for (var a=0u;a<${B}u;a=a+1u){ feat[a*${IN}u+${FS}u+o]=max(acc[a],0.0); } }
  workgroupBarrier();
  ${mlp('opacity', 'sop')}
  ${mlp('color', 'scol')}
  ${mlp('cov', 'scov')}
  ${mlp('offset', 'sofs')}
  for (var slot=tid; slot<${B}u*d.K; slot=slot+128u){
    let a=slot/d.K; let k=slot%d.K; let i=i0+a;
    if (i>=d.N){ continue; }
    let o=sop[a*${O.opacity.OUT}u+k]; if (o<=d.keep){ continue; }
    let cb=k*3u; let sb=k*7u;
    let su0=unpack2x16float(scaling[i*3u]); let su1=unpack2x16float(scaling[i*3u+1u]); let su2=unpack2x16float(scaling[i*3u+2u]);
    let st0=su0.x; let st1=su0.y; let st2=su1.x; let bs0=su1.y; let bs1=su2.x; let bs2=su2.y;
    let ax=anchor[i*3u]; let ay=anchor[i*3u+1u]; let az=anchor[i*3u+2u];
    let sx=bs0*sig(scov[a*${O.cov.OUT}u+sb]); let sy=bs1*sig(scov[a*${O.cov.OUT}u+sb+1u]); let sz=bs2*sig(scov[a*${O.cov.OUT}u+sb+2u]);
    let x=ax+sofs[a*${O.offset.OUT}u+cb]*st0; let y=ay+sofs[a*${O.offset.OUT}u+cb+1u]*st1; let z=az+sofs[a*${O.offset.OUT}u+cb+2u]*st2;
    var q=vec4<f32>(scov[a*${O.cov.OUT}u+sb+3u],scov[a*${O.cov.OUT}u+sb+4u],scov[a*${O.cov.OUT}u+sb+5u],scov[a*${O.cov.OUT}u+sb+6u]); q=q/max(length(q),1e-9);
    let oc=clamp(o,1e-6,1.0-1e-6);
    let bb=atomicAdd(&counter,1u)*17u;
    if (bb+16u >= arrayLength(&outv)) { continue; }
    outv[bb]=x; outv[bb+1u]=y; outv[bb+2u]=z; outv[bb+3u]=0.0; outv[bb+4u]=0.0; outv[bb+5u]=0.0;
    outv[bb+6u]=(scol[a*${O.color.OUT}u+cb]-0.5)/C0; outv[bb+7u]=(scol[a*${O.color.OUT}u+cb+1u]-0.5)/C0; outv[bb+8u]=(scol[a*${O.color.OUT}u+cb+2u]-0.5)/C0;
    outv[bb+9u]=log(oc/(1.0-oc));
    outv[bb+10u]=log(max(sx,1e-9)); outv[bb+11u]=log(max(sy,1e-9)); outv[bb+12u]=log(max(sz,1e-9));
    outv[bb+13u]=q.x; outv[bb+14u]=q.y; outv[bb+15u]=q.z; outv[bb+16u]=q.w;
  }
}`;
}

// 建立共用 device（一次），給所有 clip 的 decoder 共用。
// onGpuInfo(text, isSoftware)：回報 adapter 資訊給 UI（取代原版寫 #gpu DOM 的做法）。
export async function createSharedDevice(log = console.log, needBytes = 512 << 20, onGpuInfo = null) {
  if (!navigator.gpu) throw new Error('無 WebGPU');
  const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  if (!adapter) throw new Error('WebGPU adapter 取得失敗');
  let info = {}; try { info = adapter.info || (adapter.requestAdapterInfo ? await adapter.requestAdapterInfo() : {}); } catch (e) {}
  const sw = (info.architecture || '').includes('swiftshader') || (info.vendor || '') === 'google' || adapter.isFallbackAdapter;
  const gpuTxt = `GPU: ${info.vendor || '?'} / ${info.architecture || info.description || info.device || '?'}` + (sw ? '  ⚠️ 軟體模擬（非真顯卡！用 --ignore-gpu-blocklist 啟動 Chrome）' : '  ✓ 硬體加速');
  log(`GPU adapter → ${gpuTxt}`);
  if (onGpuInfo) onGpuInfo(gpuTxt, sw);
  const lim = adapter.limits;
  const device = await adapter.requestDevice({ requiredLimits: {
    maxStorageBufferBindingSize: Math.min(lim.maxStorageBufferBindingSize, needBytes),
    maxBufferSize: Math.min(lim.maxBufferSize, needBytes) } });
  device.lost.then((info) => {
    console.error('⚠️ WebGPU DEVICE LOST:', info.reason, info.message);
    if (onGpuInfo) onGpuInfo('⚠️ GPU device lost: ' + info.message, true);
  });
  device.addEventListener('uncapturederror', (e) => console.error('⚠️ GPU 第一個錯誤（根因）:', e.error.message));
  return device;
}

export async function createDecoder(clip, dir, manifest, log = console.log, device = null) {
  const hg = clip.hashgrid, N = clip.n_anchors, F = hg.n_features, L = hg.n_levels, K = manifest.n_offsets;
  const Dg = L * F; // 64
  const outCap = Math.min(N * K, 5_000_000);    // 可見高斯實測 ~3M；5M 緩衝，超過則丟棄並警告
  const need = Math.max(hg.n_params * 4, N * Dg * 4, outCap * 17 * 4) + (32 << 20);
  if (!device) device = await createSharedDevice(log, need);

  // 載入資料
  log('載入 hashgrid 參數 + 權重 + anchor/feat/scaling…');
  const tl0 = performance.now();
  // hashgrid/fused/decode 權重永遠 per-clip 並行下載
  const [pHash, pFused, pDecw] = await Promise.all([
    fbuf(`${dir}/hashgrid.f16`), fbuf(`${dir}/fused.bin`), fbuf(`${manifest._dataRoot}/decode_mlps.bin`)]);
  const tDl = performance.now();
  console.log(`  [load] 下載 hashgrid+權重 ${(tDl - tl0) | 0}ms（hashgrid ${(pHash.byteLength / 1048576).toFixed(1)}MB）`);
  const params = new Uint16Array(pHash);
  const fused = new Float32Array(pFused);
  const decw = new Float32Array(pDecw);
  // anchor/feat/scaling：有 base 就「共用 base + 本 clip 殘差」，否則整份 per-clip
  let anchor, feat, scaling;
  if (manifest.base && !clip.no_base) {   // 有 base 且此 clip 用 base（no_base 的 fallback clip 走完整檔）
    const base = await loadBase(manifest, log);
    const resN = N - base.N;
    if (resN > 0 && clip.files && clip.files.feat) {   // source clip：base + 殘差
      const [pA, pF, pS] = await Promise.all([fbuf(`${dir}/anchor.f32`), fbuf(`${dir}/feat.f16`), fbuf(`${dir}/scaling.f16`)]);
      anchor = catF32(base.anchor, new Float32Array(pA));
      feat = catU16(base.feat, new Uint16Array(pF));
      scaling = catU16(base.scaling, new Uint16Array(pS));
    } else {                                            // 參考 clip：只用 base
      anchor = base.anchor; feat = base.feat; scaling = base.scaling;
    }
  } else {
    const [pA, pF, pS] = await Promise.all([fbuf(`${dir}/anchor.f32`), fbuf(`${dir}/feat.f16`), fbuf(`${dir}/scaling.f16`)]);
    anchor = new Float32Array(pA); feat = new Uint16Array(pF); scaling = new Uint16Array(pS);
  }

  const entryOff = new Uint32Array(hg.offsets.map(o => o / F));
  const res = new Uint32Array(hg.res), dense = new Uint32Array(hg.dense), scales = new Float32Array(hg.scales);

  const owned = [];  // 此 decoder 自己的 buffer，destroy() 時釋放（不含 decodeToGPU 產出的幀 buffer）
  const ST = GPUBufferUsage.STORAGE, CD = GPUBufferUsage.COPY_DST, CS = GPUBufferUsage.COPY_SRC;
  const mk = (arr, usage) => { const b = device.createBuffer({ size: arr.byteLength, usage }); device.queue.writeBuffer(b, 0, arr); owned.push(b); return b; };
  const mkb = (size, usage) => { const b = device.createBuffer({ size, usage }); owned.push(b); return b; };
  const bParams = mk(params, ST | CD), bAnchor = mk(anchor, ST | CD), bFeat = mk(feat, ST | CD), bScal = mk(scaling, ST | CD);
  const bRes = mk(res, ST | CD), bDense = mk(dense, ST | CD), bOff = mk(entryOff, ST | CD), bScales = mk(scales, ST | CD);
  const bFused = mk(fused, ST | CD), bDecw = mk(decw, ST | CD);
  const bG = mkb(N * Dg * 4, ST);
  const bOut = mkb(outCap * 17 * 4, ST | CS);
  const bCount = mkb(4, ST | CD | CS);
  const bMeta = mkb(64, GPUBufferUsage.UNIFORM | CD);
  const bD = mkb(16, GPUBufferUsage.UNIFORM | CD);
  const bReadCount = mkb(4, GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ);
  let bReadOut = null;  // 只有 decode()（readback 路徑）才需要；播放走 decodeToGPU 不配置（省 340MB/clip）

  // pipeline 編一次、全 clip 共用（同 dataset 的 shader 完全相同）。
  // device 換了（component 重掛載）就重編 — 舊 pipeline 綁著已 destroy 的 device 不能用。
  const _pkey = `${manifest._dataRoot}|${Dg}|${manifest.n_offsets}`;
  let _pipes = _pipeCache[_pkey];
  if (!_pipes || _pipes.device !== device) {
    log('編譯 WGSL pipeline（只一次）…');
    const m1 = device.createShaderModule({ code: WGSL_HASH });
    const m2 = device.createShaderModule({ code: buildDecodeWGSL(manifest, Dg) });
    _pipes = _pipeCache[_pkey] = {
      device,
      p1: device.createComputePipeline({ layout: 'auto', compute: { module: m1, entryPoint: 'main' } }),
      p2: device.createComputePipeline({ layout: 'auto', compute: { module: m2, entryPoint: 'main' } }),
      pPack: FRAME_F16 ? device.createComputePipeline({ layout: 'auto', compute: { module: device.createShaderModule({ code: WGSL_PACK }), entryPoint: 'main' } }) : null,
    };
  }
  const { p1, p2, pPack } = _pipes;
  const bPackCnt = FRAME_F16 ? mkb(16, GPUBufferUsage.UNIFORM | CD) : null;
  const bg1 = device.createBindGroup({ layout: p1.getBindGroupLayout(0), entries: [
    { binding:0,resource:{buffer:bMeta}},{binding:1,resource:{buffer:bParams}},{binding:2,resource:{buffer:bAnchor}},
    { binding:3,resource:{buffer:bRes}},{binding:4,resource:{buffer:bDense}},{binding:5,resource:{buffer:bOff}},
    { binding:6,resource:{buffer:bScales}},{binding:7,resource:{buffer:bG}}]});
  const bg2 = device.createBindGroup({ layout: p2.getBindGroupLayout(0), entries: [
    { binding:0,resource:{buffer:bD}},{binding:1,resource:{buffer:bG}},{binding:2,resource:{buffer:bFeat}},
    { binding:3,resource:{buffer:bScal}},{binding:4,resource:{buffer:bAnchor}},{binding:5,resource:{buffer:bFused}},
    { binding:6,resource:{buffer:bDecw}},{binding:7,resource:{buffer:bOut}},{binding:8,resource:{buffer:bCount}}]});

  const GRIDW = 4096;  // pass2 用 2D dispatch（workgroup 數=N 超過 65535 單維上限）
  log(`載入+建 buffer 完成 ${(performance.now() - tl0) | 0}ms`);

  const bmin = hg.xyz_bound_min, bmax = hg.xyz_bound_max;
  async function decode(t, keep = 0.05) {
    const ab = new ArrayBuffer(64);
    new Uint32Array(ab, 0, 4).set([N, L, F, 1 << hg.log2_hashmap_size]);
    new Float32Array(ab, 16, 4).set([bmin[0], bmin[1], bmin[2], 0]);
    new Float32Array(ab, 32, 4).set([bmax[0], bmax[1], bmax[2], 0]);
    new Float32Array(ab, 48, 4).set([t, 0, 0, 0]);
    device.queue.writeBuffer(bMeta, 0, ab);
    device.queue.writeBuffer(bD, 0, new Uint32Array([N, K, GRIDW]));
    device.queue.writeBuffer(bD, 12, new Float32Array([keep]));
    device.queue.writeBuffer(bCount, 0, new Uint32Array([0]));

    const tc0 = performance.now();
    // pass1（hashgrid）獨立 submit 以分別計時
    const e1 = device.createCommandEncoder();
    let pass = e1.beginComputePass(); pass.setPipeline(p1); pass.setBindGroup(0, bg1); pass.dispatchWorkgroups(Math.ceil(N / 64)); pass.end();
    device.queue.submit([e1.finish()]);
    await device.queue.onSubmittedWorkDone();
    const tp1 = performance.now();
    // pass2（fused+4 MLP+組裝+壓實）
    const e2 = device.createCommandEncoder();
    pass = e2.beginComputePass(); pass.setPipeline(p2); pass.setBindGroup(0, bg2); pass.dispatchWorkgroups(GRIDW, Math.ceil(Math.ceil(N / DEC_B) / GRIDW)); pass.end();
    e2.copyBufferToBuffer(bCount, 0, bReadCount, 0, 4);
    device.queue.submit([e2.finish()]);
    await bReadCount.mapAsync(GPUMapMode.READ);
    let count = new Uint32Array(bReadCount.getMappedRange().slice(0))[0]; bReadCount.unmap();
    if (count > outCap) { console.warn(`可見高斯 ${count} 超過緩衝 ${outCap}，已截斷`); count = outCap; }
    const tc1 = performance.now();
    console.log(`  [decode] pass1(hashgrid) ${(tp1 - tc0) | 0}ms, pass2(decode) ${(tc1 - tp1) | 0}ms`);

    if (!bReadOut) bReadOut = mkb(outCap * 17 * 4, GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ);
    const enc2 = device.createCommandEncoder();
    enc2.copyBufferToBuffer(bOut, 0, bReadOut, 0, count * 17 * 4);
    device.queue.submit([enc2.finish()]);
    await bReadOut.mapAsync(GPUMapMode.READ, 0, count * 17 * 4);
    const verts = new Float32Array(bReadOut.getMappedRange(0, count * 17 * 4).slice(0)); bReadOut.unmap();
    console.log(`  [decode] compute(+首次上傳) ${(tc1 - tc0) | 0}ms, readback ${(performance.now() - tc1) | 0}ms`);
    return { verts, count };
  }

  // 解碼但不 readback：把壓實結果留在 GPU，回傳一個新的持久 buffer（給 WebGPU rasterizer 直接用）
  async function decodeToGPU(t, keep = 0.05) {
    const ab = new ArrayBuffer(64);
    new Uint32Array(ab, 0, 4).set([N, L, F, 1 << hg.log2_hashmap_size]);
    new Float32Array(ab, 16, 4).set([bmin[0], bmin[1], bmin[2], 0]);
    new Float32Array(ab, 32, 4).set([bmax[0], bmax[1], bmax[2], 0]);
    new Float32Array(ab, 48, 4).set([t, 0, 0, 0]);
    device.queue.writeBuffer(bMeta, 0, ab);
    device.queue.writeBuffer(bD, 0, new Uint32Array([N, K, GRIDW]));
    device.queue.writeBuffer(bD, 12, new Float32Array([keep]));
    device.queue.writeBuffer(bCount, 0, new Uint32Array([0]));
    const t0 = performance.now();
    const enc = device.createCommandEncoder();         // 單次 submit（真實解碼時間，無同步點膨脹）
    let pass = enc.beginComputePass(); pass.setPipeline(p1); pass.setBindGroup(0, bg1); pass.dispatchWorkgroups(Math.ceil(N / 64)); pass.end();
    const wg2 = Math.ceil(N / DEC_B);                  // pass2：一個 workgroup 處理 DEC_B 個 anchor
    pass = enc.beginComputePass(); pass.setPipeline(p2); pass.setBindGroup(0, bg2); pass.dispatchWorkgroups(GRIDW, Math.ceil(wg2 / GRIDW)); pass.end();
    enc.copyBufferToBuffer(bCount, 0, bReadCount, 0, 4);
    device.queue.submit([enc.finish()]);
    await bReadCount.mapAsync(GPUMapMode.READ);
    let count = new Uint32Array(bReadCount.getMappedRange().slice(0))[0]; bReadCount.unmap();
    if (count > outCap) count = outCap;
    console.log(`  [decode] ${(performance.now() - t0) | 0}ms（${count} 高斯, B=${DEC_B}）`);
    if (FRAME_F16) {
      // 打包成 f16：每顆 9 個 u32（18 f16 槽）→ ~53% 大小
      const fb = device.createBuffer({ size: count * 9 * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
      device.queue.writeBuffer(bPackCnt, 0, new Uint32Array([count, 0, 0, 0]));
      const bgPack = device.createBindGroup({ layout: pPack.getBindGroupLayout(0), entries: [
        { binding:0,resource:{buffer:bOut}},{binding:1,resource:{buffer:fb}},{binding:2,resource:{buffer:bPackCnt}}]});
      const e2 = device.createCommandEncoder();
      const pp = e2.beginComputePass(); pp.setPipeline(pPack); pp.setBindGroup(0, bgPack); pp.dispatchWorkgroups(Math.ceil(count / 64)); pp.end();
      device.queue.submit([e2.finish()]);
      return { buffer: fb, count, f16: true };
    }
    // f32：GPU→GPU 複製壓實結果（17 floats/高斯）
    const fb = device.createBuffer({ size: count * 17 * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC });
    const e2 = device.createCommandEncoder();
    e2.copyBufferToBuffer(bOut, 0, fb, 0, count * 17 * 4);
    device.queue.submit([e2.finish()]);
    return { buffer: fb, count, f16: false };
  }

  // 釋放此 decoder 的所有內部 buffer（參數 252MB + 中間 buffer），保留 decodeToGPU 產出的幀 buffer
  function destroy() { for (const b of owned) { try { b.destroy(); } catch (e) {} } owned.length = 0; }

  return { decode, decodeToGPU, N, device, destroy };
}
